/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { timeFormat, utcFormat } from 'd3';

/**
 * @module SharedModule
 */

/**
 * Check if the date range is valid and in future.
 */
export function validateDateRangeIsInFuture(referenceDate: string, date: string): boolean {
    return isDateInFuture(referenceDate) && isDateInFuture(date, referenceDate);
}

/**
 * Validate date provided is in future.
 */
export function isDateInFuture(date: string, referenceDate?: string): boolean {
    const referenceDateParsed = Date.parse(referenceDate || Date());
    const dateParsed = Date.parse(date);

    /**
     * Date.parse returns NaN for blank input
     */
    if (Number.isNaN(dateParsed)) {
        return false;
    }

    return dateParsed > referenceDateParsed;
}

/**
 * Return an object with two moment objects defining start and end of a processed period.
 * We take current time (floored by step) and add step multiplied by limit.
 */
export function getProcessedTimeFrame(step: number, limit: number): { start: Date; end: Date } {
    const currentMoment: number = Math.floor(Date.now() / 1000);
    const end: number = currentMoment - currentMoment % step;

    const start: number = end - step * (limit - 1);

    return {
        start: new Date(start * 1000),
        end: new Date(end * 1000),
    };
}

/**
 * Return the tickFormat function by evaluating the most suitable timeTickFormat.
 *
 * It takes an array of tuples containing the format string and a test function
 * that will be called with the date object as an argument. The test function
 * should return true if the format string should be used for the given date.
 *
 * The first format string for which the test function returns true will be used.
 *
 * If useUtcTime is true, the format strings will be passed to d3.utcFormat, otherwise
 * they will be passed to d3.timeFormat.
 */
export function getTickFormatFunction(
    timeTickFormats: Array<[string, (date: Date) => boolean]>,
    useUtcTime = false,
): (date: Date) => string {
    const formats = useUtcTime ?
        timeTickFormats.map(([format, test]) => [utcFormat(format), test]) :
        timeTickFormats.map(([format, test]) => [timeFormat(format), test]);

    return (date: Date): string => {
        for (const [formatFn, testFn] of formats) {
            if (testFn(date)) {
                return formatFn(date) as string;
            }
        }

        return '';
    };
}
